// From the package ROOT, not the `/countdown-bar` subpaths the storefront uses: the builder's dev server
// pre-bundles a fixed list of ui-react specifiers (`UI_REACT_ENTRIES`), and a new subpath would be served
// as a SECOND copy of the package — two React graphs for one library. Its own guard test says so.
import {
  CountdownBar,
  CountdownPopup,
  parseCountdownBar,
  parseCountdownPopup,
  type StoreLocale,
} from '@aquilixshop/ui-react';
import { useLayoutEffect, useRef, useState } from 'react';
import { useBuilderStore } from '../state/builderStore';
import { CanvasFrame } from './CanvasFrame';
import { bt } from '../i18n';

/**
 * THE COUNTDOWN APP'S TWO PAGE-WIDE SURFACES, IN THE CANVAS (feature 0041 V6, 28/09/2026).
 *
 * The buy-box banner and the card chips ride the render context, so the ordinary sections paint them here
 * exactly as they do on a live page. The BAR and the POPUP have no section to live in — on the storefront
 * it is the LAYOUT that mounts them, around the document — so the canvas has to mount them too, or the
 * editor would keep hiding two of the four surfaces the seller configured.
 *
 * The components are the STOREFRONT's own (`ui-react`), fed the same wire payload through the same
 * defensive parsers. Nothing is re-implemented for the editor: what the seller sees here is the component
 * the shop serves, with the campaign the backend resolved.
 *
 * Two honest departures from the live page, both deliberate:
 *  - the bar renders IN FLOW at the top of the canvas rather than `position: sticky`, and the popup is
 *    `absolute` inside the canvas rather than `fixed` to the window. A sticky/fixed element inside the
 *    editor would float over the inspector and the rail, and would follow the editor's scroll rather than
 *    the page's — it would be showing the seller something no visitor ever sees.
 *  - a click SELECTS the surface and opens its design panel (0041 V7); it never follows the CTA.
 *
 * ## It has to LOOK selected (04/10/2026)
 *
 * « It's not selected in the builder with the border like we do for other ». A 2px ring was painted — it
 * was there, measured — but a selected SECTION also wears its NAME on a tab at the corner, drawn by the
 * same `CanvasFrame` the inspector's selection uses. Without that tab the ring reads as part of the strip's
 * own design, not as "this is what the panel on the right is editing". So the surfaces now borrow that very
 * component: one frame, one label, whatever kind of thing is selected.
 */
export function CountdownAppChrome({ locale }: { locale: StoreLocale }) {
  const barPayload = useBuilderStore((s) => s.countdownBar);
  const popupPayload = useBuilderStore((s) => s.countdownPopup);
  const selectCountdownSurface = useBuilderStore((s) => s.selectCountdownSurface);
  const selected = useBuilderStore((s) => s.selectedCountdown);
  const previewMode = useBuilderStore((s) => s.previewMode);

  /**
   * The frame is MEASURED over a layer of its own, not drawn with CSS on the surfaces. The bubble is
   * `position: fixed`, so its wrapper has no box at all — a CSS outline on that wrapper paints nothing,
   * and the label would have nowhere to hang. The layer gives both surfaces one positioning root.
   */
  const layerRef = useRef<HTMLDivElement | null>(null);
  const barRef = useRef<HTMLDivElement | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [frameTarget, setFrameTarget] = useState<Element | null>(null);

  const selectLabel = bt('chrome.canvas.countdown_select', 'Countdown — set its design');
  const frameLabel = bt('chrome.insp.countdown_title', 'Countdown');
  const bar = parseCountdownBar(barPayload);
  const parsedPopup = parseCountdownPopup(popupPayload);
  /**
   * NO DELAY IN THE EDITOR. On a live page the bubble waits the seller's `delaySeconds` — an instant popup
   * reads as an ad, a delayed one as a reminder — and that can be up to five minutes. A seller who set
   * sixty seconds and stares at an empty corner concludes the feature is broken, so the canvas shows the
   * bubble at once. The live page keeps the delay; only the preview skips it.
   */
  const popup =
    parsedPopup && parsedPopup.popup.delaySeconds > 0
      ? { ...parsedPopup, popup: { ...parsedPopup.popup, delaySeconds: 0 } }
      : parsedPopup;
  const selectedKind = previewMode ? null : (selected?.surface ?? null);
  const selectedId = previewMode ? null : (selected?.campaignId ?? null);
  useLayoutEffect(() => {
    if (!selectedId) {
      setFrameTarget(null);
      return;
    }
    // The BUBBLE itself, not its wrapper: the wrapper is a zero-height box pinned to the canvas corner.
    const next =
      selectedKind === 'popup'
        ? (popupRef.current?.querySelector('[data-aqx-countdown-popup]') ?? null)
        : barRef.current;
    setFrameTarget(next);
  }, [selectedId, selectedKind, barPayload, popupPayload]);

  if (!bar && !popup) return null;

  return (
    <>
      {bar ? (
        <div
          ref={barRef}
          data-aqx-cd-app="bar"
          data-aqx-cd-campaign={bar.campaignId}
          data-selected={selected?.campaignId === bar.campaignId || undefined}
          data-position={bar.bar.position}
          role="button"
          tabIndex={0}
          aria-label={selectLabel}
          onClick={(e) => {
            // The strip carries a real CTA link on the storefront. In the editor a click SELECTS.
            e.preventDefault();
            e.stopPropagation();
            selectCountdownSurface({ campaignId: bar.campaignId, surface: 'bar' });
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;
            e.preventDefault();
            selectCountdownSurface({ campaignId: bar.campaignId, surface: 'bar' });
          }}
          // `contain: layout` and no pointer events: the strip is a PREVIEW of the app's bar, not a part
          // of the page being edited, so it must never eat a click meant for the section under it.
          style={{ cursor: 'pointer', position: 'relative', zIndex: 1 }}
        >
          {/* The surface itself never takes the pointer: the wrapper does, so a click anywhere on the
              strip — including on its CTA — selects instead of navigating. */}
          <div style={{ pointerEvents: 'none' }}>
            <CountdownBar bar={bar} locale={locale} />
          </div>
        </div>
      ) : null}
      {popup ? (
        <div
          data-aqx-cd-app="popup"
          style={{
            // THE BUBBLE IS `position: fixed` — the storefront wants it pinned to the window. Dropped
            // into the editor as-is it pinned itself to the EDITOR's window, floating over the inspector
            // and the rail (measured 28/09/2026: it escaped the canvas entirely). A `transform` makes
            // this box the containing block for a fixed descendant, so the bubble lands in the corner of
            // the CANVAS instead — which is the corner the seller is actually looking at.
            position: 'absolute',
            inset: 0,
            transform: 'translate(0)',
            // Under every editor affordance (the chrome ladder starts at 203) and above the page.
            zIndex: 55,
            pointerEvents: 'none',
          }}
        >
          <div
            ref={popupRef}
            data-aqx-cd-campaign={popup.campaignId}
            data-selected={selected?.campaignId === popup.campaignId || undefined}
            role="button"
            tabIndex={0}
            aria-label={selectLabel}
            style={{ pointerEvents: 'auto' }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              selectCountdownSurface({ campaignId: popup.campaignId, surface: 'popup' });
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== ' ') return;
              e.preventDefault();
              selectCountdownSurface({ campaignId: popup.campaignId, surface: 'popup' });
            }}
          >
            {/*
              LA BULLE PREND LE POINTEUR, PAS SON ENVELOPPE (04/10/2026).

              La bulle est `position: fixed` : son enveloppe est une boîte SANS HAUTEUR, donc le
              `pointer-events: auto` posé dessus ne couvrait aucun pixel — et le `none` posé ici retirait
              le seul élément qu'on voyait. Résultat mesuré : un clic sur la bulle atterrissait sur la
              section d'en dessous, et la bulle était la seule surface impossible à sélectionner.
              Le clic remonte quand même à l'enveloppe (l'arbre DOM, pas la géométrie), où il est arrêté.
            */}
            <div style={{ pointerEvents: 'auto' }}>
              <CountdownPopup popup={popup} locale={locale} />
            </div>
          </div>
        </div>
      ) : null}
      {/*
        THE NAME TAB, over a layer of its own.

        `ring={false}` for the same reason a selected SECTION passes it: the surface already wears its own
        outline (shell.css), and a second line over it would be two frames for one thing. The layer sits at
        the chrome ladder's frame level so the tab clears the bubble it labels.
      */}
      <div
        ref={layerRef}
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 203 }}
      >
        <CanvasFrame
          pageRef={layerRef}
          target={frameTarget}
          label={frameLabel}
          ring={false}
          zIndex={203}
        />
      </div>
    </>
  );
}
